// @ts-nocheck
import React, { useState, useCallback, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, Dimensions, Modal } from 'react-native';
import DbIcon from '../DbIcon';
import DbButton from '../DbButton';
import type { OrderActionBarProps, OrderActionButton } from './DbOrderCard.types';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

/**
 * OrderActionBar - 订单操作栏组件
 * 
 * 特性：
 * - 最多显示3个操作按钮
 * - 多余的按钮放入"更多"下拉面板
 * - 按钮支持圆角配置
 * - 下拉菜单在"..."按钮下方/上方弹出
 */
const OrderActionBar: React.FC<OrderActionBarProps> = ({
    actions = [],
    onMorePress,
    style,
    buttonRadius = 6,
    maxVisibleButtons = 3,
}) => {
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({
        top: 0,
        left: 0,
        showAbove: false,
    });
    const moreButtonRef = useRef<View>(null);

    // 获取可见按钮和更多按钮
    const visibleActions = actions.slice(0, maxVisibleButtons);
    const moreActions = actions.slice(maxVisibleButtons);
    const hasMoreActions = moreActions.length > 0;

    // 计算菜单位置
    const calculateMenuPosition = useCallback(() => {
        if (moreButtonRef.current) {
            moreButtonRef.current.measure((x, y, width, height, pageX, pageY) => {
                // 估算菜单高度：每个按钮44px + 上下padding 24px
                const contentHeight = moreActions.length * 44 + 24;
                const arrowHeight = 6;
                const spacing = 1; // 箭头和按钮之间的间距，减小到1px
                const menuHeight = contentHeight + arrowHeight + spacing;
                const spaceBelow = SCREEN_HEIGHT - pageY - height - 10;
                const spaceAbove = pageY - 10;

                // 如果下方空间不够，且上方空间更多，则显示在上方
                const showAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;

                // 调整位置，给箭头留出空间
                setMenuPosition({
                    // 面板在上方：整体位置往上移，留出箭头空间
                    // 面板在下方：整体位置需要包含箭头（箭头在上方）
                    top: showAbove
                        ? pageY - contentHeight - arrowHeight - spacing  // 面板在按钮上方
                        : pageY + height + spacing,                       // 面板在按钮下方
                    left: pageX,
                    showAbove,
                });
            });
        }
    }, [moreActions.length]);

    // 处理更多按钮点击
    const handleMorePress = useCallback(() => {
        if (hasMoreActions) {
            calculateMenuPosition();
            setShowMoreMenu(true);
        }
        onMorePress?.();
    }, [hasMoreActions, onMorePress, calculateMenuPosition]);

    // 关闭菜单
    const closeMenu = useCallback(() => {
        setShowMoreMenu(false);
    }, []);

    // 渲染单个按钮
    const renderButton = (action: OrderActionButton, index: number, isInMenu = false) => {
        // 优先使用 variant，其次根据 type 映射
        let variant: 'primary' | 'secondary' | 'outline' | 'ghost' = action.variant || 'outline';
        
        // 如果没有指定 variant，根据 type 映射
        if (!action.variant) {
            if (action.type === 'primary') {
                variant = 'primary';
            } else if (action.type === 'secondary') {
                variant = 'secondary';
            } else if (action.type === 'danger') {
                variant = 'primary'; // danger 也用 primary 变体，但颜色不同
            }
        }
        
        // 确定尺寸：优先使用 action.size，默认 small
        let size: 'small' | 'medium' | 'large' | 'mini' = action.size || 'small';
        
        // 自定义样式（覆盖背景色、文字颜色等）
        const customStyle: any = {
            borderRadius: buttonRadius,
        };
        
        // danger 类型或自定义背景色
        if (action.backgroundColor) {
            customStyle.backgroundColor = action.backgroundColor;
        } else if (action.type === 'danger') {
            customStyle.backgroundColor = '#FF3B30';
        }
        
        // mini 尺寸的特殊样式（比 small 更小）
        if (size === 'mini') {
            customStyle.paddingHorizontal = 8;
            customStyle.paddingVertical = 2;
            customStyle.minHeight = 24;
        }
        
        // 自定义文字样式
        const customTextStyle: any = {};
        if (action.textColor) {
            customTextStyle.color = action.textColor;
        } else if (action.type === 'danger' && variant === 'primary') {
            customTextStyle.color = '#fff';
        }
        
        // mini 尺寸的文字更小
        if (size === 'mini') {
            customTextStyle.fontSize = 12;
        }

        return (
            <DbButton
                key={`${action.text}-${index}`}
                title={action.text}
                variant={variant}
                size={size === 'mini' ? 'small' : size} // DbButton 不支持 mini，用 small 替代
                style={[styles.button, customStyle, isInMenu && styles.menuButton]}
                textStyle={[styles.buttonText, customTextStyle]}
                onPress={() => {
                    action.onPress?.();
                    if (isInMenu) {
                        closeMenu();
                    }
                }}
            />
        );
    };

    // 渲染更多菜单
    const renderMoreMenu = () => {
        if (!showMoreMenu) return null;

        return (
            <Modal
                visible={showMoreMenu}
                transparent
                animationType="none"
                onRequestClose={closeMenu}
            >
                {/* 透明遮罩 - 用于捕获点击关闭 */}
                <TouchableOpacity
                    style={styles.transparentOverlay}
                    activeOpacity={1}
                    onPress={closeMenu}
                >
                    {/* 菜单内容 - 绝对定位 */}
                    <View
                        style={[
                            styles.menuContainer,
                            {
                                top: menuPosition.top,
                                left: menuPosition.left,
                            },
                        ]}
                        pointerEvents="box-none"
                    >
                        {menuPosition.showAbove ? (
                            <>
                                {/* 面板在上方：菜单在上，箭头在下 */}
                                <View style={styles.menuContent}>
                                    {moreActions.map((action, index) => renderButton(action, index, true))}
                                </View>
                                <View style={[styles.arrow, styles.arrowDown]} />
                            </>
                        ) : (
                            <>
                                {/* 面板在下方：箭头在上，菜单在下 */}
                                <View style={[styles.arrow, styles.arrowUp]} />
                                <View style={styles.menuContent}>
                                    {moreActions.map((action, index) => renderButton(action, index, true))}
                                </View>
                            </>
                        )}
                    </View>
                </TouchableOpacity>
            </Modal>
        );
    };

    return (
        <View style={[styles.container, style]}>
            {/* 更多按钮 - 靠左显示 */}
            {hasMoreActions && (
                <TouchableOpacity
                    ref={moreButtonRef}
                    style={styles.moreButton}
                    onPress={handleMorePress}
                    activeOpacity={0.7}
                >
                    <DbIcon name="ellipsis-horizontal" family="Ionicons" size={20} color="#666" />
                </TouchableOpacity>
            )}

            {/* 操作按钮 - 靠右显示 */}
            <View style={styles.buttonsContainer}>
                {visibleActions.map((action, index) => renderButton(action, index))}
            </View>

            {/* 更多菜单 */}
            {renderMoreMenu()}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 12,
        flex: 1,
    },
    moreButton: {
        padding: 8,
        marginRight: 8,
    },
    buttonsContainer: {
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'flex-end',
        flex: 1,
    },
    button: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        minWidth: 64,
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'flex-start', // 防止按钮扩展占满整行
    },
    buttonText: {
        fontSize: 12,
        fontWeight: '500',
    },
    transparentOverlay: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    menuContainer: {
        position: 'absolute',
        minWidth: 120,
    },
    arrow: {
        width: 0,
        height: 0,
        backgroundColor: 'transparent',
        borderStyle: 'solid',
        marginLeft: 8,
    },
    arrowUp: {
        borderLeftWidth: 5,
        borderRightWidth: 5,
        borderBottomWidth: 6,
        borderLeftColor: 'transparent',
        borderRightColor: 'transparent',
        borderBottomColor: '#ffffff',
    },
    arrowDown: {
        borderLeftWidth: 5,
        borderRightWidth: 5,
        borderTopWidth: 6,
        borderLeftColor: 'transparent',
        borderRightColor: 'transparent',
        borderTopColor: '#ffffff',
    },
    menuContent: {
        backgroundColor: '#ffffff',
        borderRadius: 8,
        padding: 12,
        gap: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.15,
        shadowRadius: 6,
        elevation: 6,
    },
    menuAbove: {
        // 当菜单显示在上方时的特殊样式
    },
    menuButton: {
        width: '100%',
        marginVertical: 2,
    },
});

export default OrderActionBar;
